<template>
	<div
		v-if="selectedTile"
		class="tile-detail-view"
		:key="dynamicTileKey"
		:class="{ 'pointer-events-none': isCameraActive }"
	>
		<ModalWindow
			:eventType="tileEventType(selectedTile.frameName)"
			:heading="computedTileHeading"
			:subHeading="computedTileSubHeading"
			:fullWidthContent="isSettlement"
			:skin="modalWindowSkin"
			:headerData="
				isSettlement
					? {
							level: currentSettlementLevel,
							gridToggle: false,
							context: UIContexts.SETTLEMENT,
							isAvailableUpgrade: isAvailableSettlementUpgrade,
						}
					: { level: null, gridToggle: true }
			"
		>
			<template v-if="isShopPromoted" #topBar>
				<div class="tile-detail-view__top-bar">
					<div class="navigation__item-icon icon-shop-38" />
					<AppTextButton
						:text="translate('button.visit_shop')"
						:type="StatusTypes.PRIMARY"
						:fontSize="12"
						@click="handleVisitShop"
					/>
				</div>
			</template>
			<template #content>
				<SettlementTileDetailView
					v-if="isSettlement"
					:is-connected="settlementIsConnected"
					:tile-index="selectedTile.index"
					@isAvailableUpgrade="
						handleSettlementAvailableUpgrade($event)
					"
				/>
				<div v-else class="tile-detail-view__section">
					<div
						class="tile-detail-view__section-box"
						v-if="
							!(
								selectedTile &&
								isEnemiesTile &&
								isEnemiesUnlocked
							)
						"
					>
						<div
							v-if="!isTerrainDetail"
							class="tile-detail-view__section-image"
							:class="[
								`icon-${currentTileIcon}-80`,
								{
									'tile-detail-view__section-image--event':
										modalWindowSkin ===
										ModalWindowSkinTypes.EVENT,
								},
							]"
						/>
						<div
							class="tile-detail-view__section-content"
							:class="{
								'section-content--slim': isTerrainDetail,
							}"
						>
							<p
								class="tile-detail-view__section-description"
								v-show="tileDescription.length > 0"
							>
								{{ tileDescription }}
							</p>
							<!-- prettier-ignore -->
							<TileAggregationDetails
								:entity="(selectedTile as unknown as TileEntity)"
								:isPromotion="false"
							/>
							<div
								class="tile-detail-view__section-limit"
								v-if="selectedTile.buildingLimit"
							>
								<div>{{ `${translate("ui.limit")}:` }}</div>
								<div
									:class="{
										'full-limit':
											selectedTile.sameBuildingCount +
												(buildingsStorageStore?.[
													selectedTile.frameName
												] || 0) >=
											selectedTile.buildingLimit,
									}"
								>
									{{
										`${selectedTile.sameBuildingCount}/${selectedTile.buildingLimit}`
									}}
								</div>
							</div>
						</div>
					</div>
					<FightSystemTileDetailView
						v-if="
							selectedTile && isEnemiesTile && isEnemiesUnlocked
						"
						:currentTileIcon="currentTileIcon"
						:entity="selectedTile as TileEntity"
					>
						<div
							v-if="
								tilerepoFriendlySupplyUnitsFrameNames.has(
									selectedTile.frameName,
								)
							"
							class="tile-detail-view__section-box tile-detail-view__section-box--fight-system"
						>
							<div
								v-if="!isTerrainDetail"
								class="tile-detail-view__section-image"
								:class="[
									`icon-${currentTileIcon}-80`,
									{
										'tile-detail-view__section-image--event':
											modalWindowSkin ===
											ModalWindowSkinTypes.EVENT,
									},
								]"
							/>
							<div
								class="tile-detail-view__section-content"
								:class="{
									'section-content--slim': isTerrainDetail,
								}"
							>
								<p
									class="tile-detail-view__section-description"
									v-show="tileDescription.length > 0"
								>
									{{ tileDescription }}
								</p>
								<!-- prettier-ignore -->
								<TileAggregationDetails
								:entity="(selectedTile as unknown as TileEntity)"
								:isPromotion="false"
							/>
							</div>
						</div>
					</FightSystemTileDetailView>
					<div
						v-if="
							(selectedTile.isBlockedByEnemy &&
								selectedTile.isBlockedBy !== 'enemies') ||
							(selectedTile.isBlockedBy === 'enemies' &&
								!isEnemiesUnlocked)
						"
						class="out-of-order-section"
					>
						<span
							v-if="selectedTile.isBlockedBy === 'bandits'"
							class="no-uppercase"
						>
							{{ translate("terrain.enemy_nearby") }}
						</span>
						<span
							v-else-if="selectedTile.isBlockedBy === 'pirates'"
							class="no-uppercase"
						>
							{{ translate("terrain.pirates_nearby") }}
						</span>
						<template
							v-else-if="
								!isEnemiesUnlocked &&
								selectedTile.isBlockedBy === 'enemies'
							"
						>
							<span class="no-uppercase">
								{{ translate("ui.enemy_territory") }}
							</span>
							<span class="no-uppercase">
								{{
									translate("ui.battle_from_level").replace(
										"{number}",
										`${ssr.unlockLevel}`,
									)
								}}
							</span>
						</template>
					</div>
					<div
						v-else
						class="tile-detail-view__section-options option-collection"
					>
						<div
							class="out-of-order-section"
							v-if="
								(isEnemiesTile && !isEnemiesUnlocked) ||
								buildingIsOutOfOrder ||
								(tilerepoSettlementEmptyFrameNames.has(
									selectedTile.frameName,
								) &&
									!settlementIsConnected)
							"
						>
							<template
								v-if="isEnemiesTile && !isEnemiesUnlocked"
							>
								<span class="no-uppercase">
									{{ translate("ui.enemy_territory") }}
								</span>
								<span class="no-uppercase">
									{{
										translate(
											"ui.battle_from_level",
										).replace(
											"{number}",
											`${ssr.unlockLevel}`,
										)
									}}
								</span>
							</template>
							<template v-else-if="buildingIsOutOfOrder">
								<span>
									{{ translate("ui.out_of_order") }}
								</span>
								<span
									v-if="
										selectedTile.frameName !==
										FrameName.PORT
									"
								>
									{{ translate("ui.connect_road") }}
								</span>
							</template>
							<span v-else-if="!settlementIsConnected">
								{{ translate("ui.connect_road_town") }}
							</span>
						</div>
						<div
							class="event-building-label"
							v-if="
								tilerepoEventBuildingsFrameNames.has(
									selectedTile.frameName,
								)
							"
							:data-event-type="
								tileEventType(selectedTile.frameName)
							"
						>
							<span>
								{{ translate("event.event_building") }}
							</span>
							<Tooltip
								:placement="Placements.TOP"
								v-if="
									tilerepoRemovableEventBuildingsFrameNames.has(
										selectedTile.frameName,
									)
								"
							>
								<template #element>
									<AppIconButton :type="IconTypes.INFO" />
								</template>
								<template #tooltip>
									<span>
										{{
											translate(
												"event.event_building_validity",
											)
										}}
									</span>
								</template>
							</Tooltip>
						</div>

						<section class="remove-section">
							<div
								class="demolish-section"
								v-if="
									selectedTile.isDestroyable ||
									selectedTile.isCuttable ||
									(selectedTile.isRemovable &&
										!selectedTile.isFarmField)
								"
								:class="{
									'demolish-section--disabled':
										selectedTile.isDemolishDisabled,
								}"
								@click="
									handleUpgrade(
										selectedTile.isCuttable ||
											(selectedTile.isRemovable &&
												!selectedTile.isFarmField)
											? FrameName.GRASSLAND
											: FrameName.NOT_SET,
										false,
										undefined,
										undefined,
										isStorable(selectedTile.frameName),
									)
								"
							>
								<ScrollFade
									:direction="ScrollDirection.HORIZONTAL"
								>
									<template #scrollContent>
										<div class="demolish-section__wrapper">
											<div
												v-if="showPutInInventoryIcon"
												:class="`icon-put-in-inventory-48`"
											/>
											<div
												v-else
												class="demolish-section__icon"
												:class="`icon-light-${
													selectedTile.action
														? selectedTile.action
														: TileAction.DEMOLISH
												}-32`"
											/>
											<div
												class="demolish-section__content"
											>
												<span
													class="demolish-section__label"
												>
													{{
														translate(demolishText)
													}}
												</span>
												<div
													v-if="
														demolishPrice &&
														Object.keys(
															demolishPrice,
														).length
													"
													class="demolish-section__price"
												>
													<template
														v-if="
															!selectedTile.isDemolishDisabled ||
															selectedTile.isBlockedByEnemy
														"
													>
														<span
															class="price-label"
														>
															{{
																`${translate("ui.price")}:`
															}}
														</span>
														<AppPrice
															v-for="(
																value,
																currency,
																index
															) in demolishPrice"
															:key="index"
															:amount="value!"
															:currency="
																currency as ResourceType
															"
														/>
													</template>
													<p
														v-else-if="
															!tilerepoForestFrameNames.has(
																selectedTile.frameName,
															) &&
															selectedTile.requirements.has(
																ResourceType.LEVEL,
															)
														"
														class="price-label"
													>
														{{
															translate(
																"ui.available_from_level",
															).replace(
																"{number}",
																selectedTile.requirements
																	.get(
																		ResourceType.LEVEL,
																	)!
																	.toString(),
															)
														}}
													</p>
													<p
														v-else-if="
															tilerepoForestFrameNames.has(
																selectedTile.frameName,
															)
														"
														class="price-label"
													>
														{{
															translate(
																"ui.available_from_level",
															).replace(
																"{number}",
																CUTTABLE_LEVEL.toString(),
															)
														}}
													</p>
												</div>
												<div
													v-if="
														rewardPrice &&
														Object.keys(rewardPrice)
															.length
													"
													class="demolish-section__price"
												>
													<span class="price-label">
														{{
															`${translate("ui.you_get")}:`
														}}
													</span>
													<AppPrice
														v-for="(
															amount,
															currency,
															index
														) in rewardPrice"
														:key="index"
														:amount="amount!"
														:currency="
															currency as ResourceType
														"
														:resourceCheck="false"
													/>
												</div>
											</div>
										</div>
									</template>
								</ScrollFade>
							</div>
							<AppIconButton
								v-if="
									showPutInInInventoryButton &&
									!(isEnemiesTile && isEnemiesUnlocked)
								"
								class="put-in-inventory"
								:type="IconTypes.PUT_IN_INVENTORY"
								:iconSize="48"
								@click="handlePutInInventory"
							/>
							<div
								class="move-button"
								v-if="
									tilerepoFriendlyUnitsFrameNames.has(
										selectedTile.frameName,
									)
								"
								@click="move"
							>
								<div class="icon-light-move-32" />
							</div>
						</section>
						<template v-if="selectedTile.isDefeatable">
							<AppTextButton
								v-if="!!selectedTile.skipDefeatGemsPrice"
								class="bribe-button"
								:type="StatusTypes.SECONDARY"
								:text="translate('button.bribe_action')"
								@click="openBribePopup"
							/>
						</template>
						<div
							v-if="
								!isEnemiesTile ||
								(isEnemiesTile &&
									ssr.isWholeEnemyTerritoryRevealedByTileIndex(
										selectedTile.index,
									))
							"
							v-show="computedEntities.length > 0"
							class="option-collection__items"
							:class="`option-collection__items--${tileDetailGridView ? 'grid' : 'list'}`"
						>
							<template
								v-for="(entity, index) in computedEntities"
								:key="index"
							>
								<TutorialStep
									:type="TutorialStepTypes.GLOW"
									v-if="isTutorialGlow()"
								>
									<template #default>
										<TileUpgradeBox
											:key="index"
											@click="
												handleUpgrade(
													entity.frameName,
													true,
													entity.name,
												)
											"
											:heading="entity.name"
											:description="
												switchParagraphFormat(
													entity.description,
												)
											"
											:type="
												tileDetailGridView
													? TileUpgradeBoxTypes.GRID
													: TileUpgradeBoxTypes.LIST
											"
											:skin="
												getBoxTypeSkin(entity.frameName)
											"
											:iconName="
												getTileIconName(
													entity.frameName,
												)
											"
											:notification="
												entity.notification || 0
											"
											:is-disabled="isDisabled(entity)"
											:price="priceValue(entity)"
											:consumption="
												entity?.consumption ?? null
											"
											:entity="entity"
											:selectedEntity="
												selectedTile as unknown as TileEntity
											"
											:fewShipyards="
												fewShipyards(entity.frameName)
											"
											:hasEnoughtSettlementLevelForPremiumRoads="
												hasEnoughtSettlementLevelForPremiumRoads(
													entity.frameName,
												)
											"
											:eventType="
												tileEventType(entity.frameName)
											"
											isPromotion
										/>
									</template>
								</TutorialStep>
								<TileUpgradeBox
									v-else
									@click="
										handleUpgrade(
											entity.frameName,
											true,
											entity.name,
											false,
											false,
											limitReached(entity),
										)
									"
									:heading="entity.name"
									:description="
										switchParagraphFormat(
											entity.description,
										)
									"
									:type="
										tileDetailGridView
											? TileUpgradeBoxTypes.GRID
											: TileUpgradeBoxTypes.LIST
									"
									:skin="getBoxTypeSkin(entity.frameName)"
									:iconName="
										getTileIconName(entity.frameName)
									"
									:notification="entity.notification || 0"
									:isDisabled="isDisabled(entity)"
									:price="priceValue(entity)"
									:consumption="entity?.consumption ?? null"
									:entity="entity"
									:selectedEntity="
										selectedTile as unknown as TileEntity
									"
									:fewShipyards="
										fewShipyards(entity.frameName)
									"
									:hasEnoughtSettlementLevelForPremiumRoads="
										hasEnoughtSettlementLevelForPremiumRoads(
											entity.frameName,
										)
									"
									:eventType="tileEventType(entity.frameName)"
									isPromotion
								/>
							</template>
						</div>
					</div>
					<HorizontalDecorDivider v-if="recommendedUpgrade" />
					<div
						class="tile-detail-view__upgrade"
						v-if="recommendedUpgrade"
					>
						<div class="tile-detail-view__upgrade-heading">
							{{ translate("ui.recommended_upgrades") }}
						</div>
						<UpgradeBox
							:has-redirect="true"
							:skin="SkinTypes.LIGHT"
							:id="recommendedUpgrade[0]"
							:heading="recommendedUpgrade[1]?.heading.toString()"
							:description="recommendedUpgrade[1]?.description"
							:descriptionReplace="
								recommendedUpgrade[1]?.descriptionReplace
							"
						/>
					</div>
				</div>
				<HorizontalDecorDivider v-if="isPOI" :isCentered="true" />
				<!-- prettier-ignore -->
				<POITileDetailView
					v-if="isPOI"
					:tile="(selectedTile as AbstractPoiTileEntity)"
				/>
			</template>
		</ModalWindow>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ModalWindow from "@/components/ModalWindow.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import BribePopup from "@/components/TileDetail/BribePopup.vue";
import TileUpgradeBox from "@/components/TileUpgradeBox.vue";
import Tooltip from "@/components/Tooltip.vue";
import TutorialStep from "@/components/TutorialStep.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import {
	EVENT_BUILDING_UNLOCK_LEVEL,
	OFFER_BUILDING_UNLOCK_LEVEL,
} from "@/config/constants";
import { isTemporaryEventBuildingActive } from "@/event";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import {
	FrameName,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoChristmasEventBuildingsFrameNames,
	tilerepoCobbledRoadsFrameNames,
	tilerepoEasterEventBuildingsFrameNames,
	tilerepoEventBuildingsFrameNames,
	tilerepoFightableMainEnemiesFrameNames,
	tilerepoFirefestEventBuildingsFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishingEventBuildingsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoForestFrameNames,
	tilerepoFriendlySupplyUnitsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoHalloweenEventBuildingsFrameNames,
	tilerepoHuntingEventBuildingsFrameNames,
	tilerepoLayer0FrameNames,
	tilerepoNonSellableFrameNames,
	tilerepoNotStorableFrameNames,
	tilerepoPiratesEventBuildingsFrameNames,
	tilerepoPoiFrameNames,
	tilerepoPremiumBuildings,
	tilerepoRemovableEventBuildingsFrameNames,
	tilerepoRoadsFrameNames,
	tilerepoSalvageableFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoSettlementEmptyFrameNames,
	tilerepoSolarFairEventBuildingsFrameNames,
	tilerepoSpecialUnitsFrameNames,
	tilerepoStoneBridgeRoadsFrameNames,
	tilerepoTunnelRoadsFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from "@/game/entities/config";
import { isBuilding } from "@/game/entities/config/buildings";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import { CUTTABLE_LEVEL, isTerrain } from "@/game/entities/config/terrain";
import {
	type UpgradeId,
	type UpgradeItem,
	upgrades,
} from "@/game/entities/config/upgrades";
import type AbstractPoiTileEntity from "@/game/entities/poi/AbstractPoiTileEntity";
import type TileEntity from "@/game/entities/TileEntity";
import SoundService from "@/game/SoundService";
import { turnOnQuickTool } from "@/game/utils/quickTool";
import { getCurrencyValue } from "@/game/utils/resources";
import type { TileRepositoryUpgradePayload } from "@/hooks/useEmitter";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import useTileRepository from "@/hooks/useTileRepository";
import router from "@/router";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useQuickToolStore from "@/store/useQuickToolStore.ts";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore.ts";
import useUpgradesStore from "@/store/useUpgradesStore";
import { TileAction, type UpgradeParams } from "@/types/tile";
import {
	EventCategory,
	IconTypes,
	ModalWindowSkinTypes,
	Placements,
	RouteNames,
	ScrollDirection,
	SkinTypes,
	StatusTypes,
	TileUpgradeBoxSkinTypes,
	TileUpgradeBoxTypes,
	TutorialStepTypes,
	UIContexts,
} from "@/types/ui";
import { QuickToolActions } from "@/types/upgrades.ts";
import { formatPrice, resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { floorNumber } from "@/utils/number";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import POITileDetailView from "@/views/TileDetail/POITileDetailView.vue";
import SettlementTileDetailView from "@/views/TileDetail/SettlementTileDetailView.vue";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
	watchEffect,
} from "vue";
import { type RouteParamsGeneric, useRoute } from "vue-router";
import FightSystemTileDetailView from "./FightSystemTileDetailView.vue";

const FRAME_NAME_SUFFIX_REGEXP = /(_\d+)?\.png$/;

const route = useRoute();

const emitter = markRaw(useEmitter());
const tilerepo = markRaw(useTileRepository());
const soundService = markRaw(SoundService.getInstance());

const popupStore = usePopupStore();

const selectedTile = ref<TileEntity | null>(null);

const runtimeStore = useRuntimeStore();
const { isPhaserReady, isCameraActive } = storeToRefs(runtimeStore);

const settingsStore = useSettingsStore();
const { tileDetailGridView, showConfirmGemsMessages } =
	storeToRefs(settingsStore);

const settlementsStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementsStore);

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);
const { gems } = resourcesStore;

const buildingsStorageStore = useBuildingsStorageStore();
const upgradesStore = useUpgradesStore();

const questStore = useQuestStore();
const { isTutorialHighlightingUI, tutorialStep } = storeToRefs(questStore);
const { setIsTutorialHighlightingUI } = questStore;

const ssr = markRaw(useSiegeSystemRepository());
const sss = markRaw(useSiegeSystemStore());
const { setBuilding, setAction, setActive, setShow, setError } =
	useQuickToolStore();

const seaFrames = new Set<FrameName>([
	FrameName.SHALLOW_SEA_SMALL_FISH,
	FrameName.SHALLOW_SEA_MEDIUM_FISH,
	FrameName.DEEP_SEA_MEDIUM_FISH,
	FrameName.DEEP_SEA_BIG_FISH,
	FrameName.SHALLOW_SEA,
	FrameName.DEEP_SEA,
]);

// NOTE: required when staying on route but params are changing
watch(
	(): RouteParamsGeneric => route.params,
	({ x, y }: RouteParamsGeneric): void => {
		if (x && y) setCurrentTile(+x, +y);
	},
);

// osetrenie cakania na init Phaser ready stav aj ked uz sme na URL adrese ale este sa len loaduje mapa
watch(
	(): boolean => isPhaserReady.value,
	(newVal: boolean): void => {
		if (newVal && route.params.x && route.params.y) {
			setCurrentTile(+route.params.x, +route.params.y);
		}
	},
);

const isDisabled = (entity: TileEntity): boolean => {
	return !!(
		(selectedTile.value?.isUpgradeToDisabled(entity.frameName) &&
			!lowGems(entity.frameName)) ||
		fewShipyards(entity.frameName) ||
		hasEnoughtSettlementLevelForPremiumRoads(entity.frameName) ||
		limitReached(entity)
	);
};

const recommendedUpgrade = computed((): [UpgradeId, UpgradeItem] | null => {
	if (!selectedTile.value?.recommendedUpgrades) return null;

	const filteredUpgrade =
		Array.from(upgrades.entries()).filter(
			(upgrade: [UpgradeId, UpgradeItem]): boolean =>
				!!selectedTile.value?.recommendedUpgrades?.includes(
					upgrade[0],
				) &&
				upgrade[1].levelUnlock <= settlementsStore.mainLevel &&
				upgradesStore[upgrade[0]] < upgrade[1].maxLevel,
		) ?? null;
	if (!filteredUpgrade || filteredUpgrade.length === 0) return null;

	const randomUpgrade = floorNumber(Math.random() * filteredUpgrade.length);
	return filteredUpgrade[randomUpgrade];
});

const settlementIsConnected = computed((): boolean => {
	return !!selectedTile.value?.isConnectedToMainSettlement;
});

const buildingIsOutOfOrder = computed((): boolean => {
	if (!selectedTile.value) return false;

	if (
		selectedTile.value.frameName !== FrameName.PORT &&
		(!selectedTile.value.requirements.has(
			ResourceType.SETTLEMENT_CONNECTION,
		) ||
			selectedTile.value.requirements.get(
				ResourceType.SETTLEMENT_CONNECTION,
			) === 0)
	)
		return false;

	return (
		isBuilding(selectedTile.value.frameName) &&
		!selectedTile.value.isConnectedToMainSettlement
	);
});

const isEnemiesUnlocked = computed((): boolean => {
	return ssr.isUnlocked;
});

const isEnemiesTile = computed((): boolean => {
	return (
		(!!selectedTile.value?.isBlockedByEnemy &&
			selectedTile.value?.isBlockedBy === "enemies") ||
		tilerepoFightableMainEnemiesFrameNames.has(
			selectedTile.value?.frameName as FrameName,
		)
	);
});

const dynamicTileKey = computed((): string => {
	return selectedTile.value
		? `${selectedTile.value.index}|${selectedTile.value.frameName.replace(FRAME_NAME_SUFFIX_REGEXP, "")}`
		: "";
});

const settlementNameArray = Array.from(settlementNames.values()).map(
	(obj: { name: string }): string => obj.name,
);

const computedTileHeading = computed((): string => {
	if (!selectedTile.value) return "";
	if (settlementNameArray.includes(selectedTile.value.name)) {
		return `${translate(FrameName.SETTLEMENT_EMPTY + ".name")} ${selectedTile.value.name}`;
	}
	return selectedTile.value.name || "Tile";
});

const tileDescription = computed((): string => {
	return selectedTile.value?.description.replace(/\{\/?b\}/g, "") ?? "";
});

const computedTileSubHeading = computed((): string | null => {
	if (!selectedTile.value) return null;

	const mapCenterPoint = tilerepo.MAP_SIZE / 2;
	const x = selectedTile.value.x - mapCenterPoint;
	const y = selectedTile.value.y - mapCenterPoint;
	const distance = Math.abs(x) + Math.abs(y);

	let text = distance === 0 ? translate("ui.map_center") : `x: ${x}, y: ${y}`;

	if (import.meta.env.DEV) {
		text += `, <span style="pointer-events:auto;user-select:all;">{x:${selectedTile.value.x}, y:${selectedTile.value.y}}</span>`;
		text += `, i:<span style="pointer-events:auto;user-select:all;">${selectedTile.value.index}</span>`;
		text += `, s:<span style="pointer-events:auto;user-select:all;">${useSegmentManager().getSegmentIdByTileIndex(selectedTile.value.index)}</span>`;
		text += `, c:${selectedTile.value.isConnectedToMainSettlement ? 1 : 0}`;
	} else {
		text += `, ${translate("ui.distance").toLowerCase()}: ${distance}`;
	}

	return text;
});

const currentTileIcon = computed((): string => {
	if (!selectedTile.value) return "";
	return getTileIconName(selectedTile.value.frameName);
});

const getBoxTypeSkin = (frameName: FrameName): TileUpgradeBoxSkinTypes => {
	if (tilerepoEventBuildingsFrameNames.has(frameName))
		return TileUpgradeBoxSkinTypes.BUILDING_EVENT;
	if (tilerepoSpecialUnitsFrameNames.has(frameName))
		return TileUpgradeBoxSkinTypes.FRIENDLY_UNIT;
	if (tilerepoFriendlySupplyUnitsFrameNames.has(frameName))
		return TileUpgradeBoxSkinTypes.DEFAULT;
	if (tilerepoFriendlyUnitsFrameNames.has(frameName))
		return TileUpgradeBoxSkinTypes.FRIENDLY_UNIT;
	if (isTerrain(frameName)) return TileUpgradeBoxSkinTypes.TERRAIN;
	if (isBuilding(frameName))
		return tilerepoPremiumBuildings.has(frameName)
			? TileUpgradeBoxSkinTypes.BUILDING_PREMIUM
			: TileUpgradeBoxSkinTypes.BUILDING;
	return TileUpgradeBoxSkinTypes.DEFAULT;
};

const modalWindowSkin = computed((): ModalWindowSkinTypes | null => {
	if (
		selectedTile.value &&
		tilerepoEventBuildingsFrameNames.has(selectedTile.value.frameName)
	) {
		return ModalWindowSkinTypes.EVENT;
	}
	return null;
});

const isPOI = computed((): boolean => {
	if (!selectedTile.value) return false;
	return tilerepoPoiFrameNames.has(selectedTile.value.frameName);
});

const isSettlement = computed((): boolean => {
	if (!selectedTile.value) return false;
	return tilerepoSettlementCastleBuildingsFrameNames.has(
		selectedTile.value.frameName,
	);
});

const currentSettlementLevel = computed((): number | null => {
	if (!selectedTile.value || !isSettlement.value) return null;
	const tile = selectedTile.value as SettlementCastleBuildingTileEntity;
	return tile.level;
});

const isTerrainDetail = computed((): boolean => {
	if (!selectedTile.value) return false;
	return tilerepoLayer0FrameNames.has(selectedTile.value.frameName);
});

const isShopPromoted = ref<boolean>(false);
const missingResource = ref<string | null>(null);

watchEffect((): void => {
	missingResource.value = null;
	isShopPromoted.value = false;

	if (settlementsStore.mainLevel < 10) return;

	if (
		isSettlement.value &&
		// upgrade blocked by missing resource
		!(selectedTile.value as SettlementCastleBuildingTileEntity)
			.canLevelUp &&
		// upgrade requirement satisfied
		// TODO: Toto sa tu vzdy vypocitava nanovo, trva to dlho.
		(selectedTile.value as SettlementCastleBuildingTileEntity)
			.isQuestCompleted
	) {
		const { price } =
			(
				selectedTile.value as SettlementCastleBuildingTileEntity
			).upgrades.get(FrameName.SETTLEMENT_CASTLE) ||
			({} as UpgradeParams);
		if (!price) {
			isShopPromoted.value = false;
			return;
		}

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;

		const missingResourceEntry = entries.find(
			([key, value]: [ResourceType, number]): boolean =>
				!resourcesStore.validateResourceAmount(key, value),
		);
		const currencyKey: ResourceType | null =
			missingResourceEntry && missingResourceEntry[0]
				? resourceTypeToCurrencyType(missingResourceEntry[0])
				: null;

		if (currencyKey) {
			missingResource.value = currencyKey;
			isShopPromoted.value = true;
			return;
		} else {
			missingResource.value = null;
			isShopPromoted.value = false;
			return;
		}
	}

	if (selectedTile.value && computedEntities.value) {
		computedEntities.value.forEach((entity: TileEntity): void => {
			const upgradePrice = selectedTile.value?.upgrades?.get(
				entity.frameName,
			)?.price as Partial<Record<ResourceType, number>> | undefined;

			const upgradePriceEntries = Object.entries(
				upgradePrice || {},
			) as Array<[ResourceType, number]>;

			if (upgradePriceEntries.length) {
				upgradePriceEntries.forEach(
					([key, value]: [ResourceType, number]): void => {
						if (!value) return;
						const currencyType = resourceTypeToCurrencyType(key);
						if (getCurrencyValue(currencyType, resources) < value) {
							if (
								missingResource.value === ResourceType.GEMS ||
								currencyType === ResourceType.GEMS
							) {
								missingResource.value = ResourceType.GEMS;
								isShopPromoted.value = true;
								return;
							}

							if (!missingResource.value) {
								missingResource.value = currencyType;
								isShopPromoted.value = true;
								return;
							}
						}
					},
				);
			} else {
				missingResource.value = null;
				isShopPromoted.value = false;
				return;
			}
		});
	}
});

const demolishText = computed((): string => {
	if (!selectedTile.value) return "";

	const isEventBuildingToDemolish =
		tilerepoRemovableEventBuildingsFrameNames.has(
			selectedTile.value.frameName,
		) && !isTemporaryEventBuildingActive(selectedTile.value.frameName);
	const isPremiumBuilding = !!tilerepoPremiumBuildings.has(
		selectedTile.value.frameName,
	);

	if (
		tilerepoFishingBoatsFrameNames.has(selectedTile.value.frameName) ||
		tilerepoBigFishingBoatsFrameNames.has(selectedTile.value.frameName)
	) {
		return "button.return_boat";
	}

	if (
		(!isEventBuildingToDemolish && isPremiumBuilding) ||
		tilerepoFishTrapsFrameNames.has(selectedTile.value.frameName)
	) {
		return "button.put_in_inventory";
	}

	return `tile_action.${
		selectedTile.value.action
			? selectedTile.value.action
			: TileAction.DEMOLISH
	}`;
});

const demolishPrice = computed(
	(): Partial<Record<ResourceType, number>> | null => {
		if (!selectedTile.value) return null;
		if (
			!selectedTile.value.upgrades.has(FrameName.NOT_SET) &&
			!selectedTile.value.upgrades.has(FrameName.GRASSLAND)
		)
			return null;

		const { price } = selectedTile.value.upgrades.get(
			selectedTile.value.isCuttable ||
				(selectedTile.value.isRemovable &&
					!selectedTile.value.isFarmField)
				? FrameName.GRASSLAND
				: FrameName.NOT_SET,
		)! as UpgradeParams;
		if (!price) return null;

		return formatPrice(selectedTile.value.resolveDynamicResources(price));
	},
);

const rewardPrice = computed(
	(): Partial<Record<ResourceType, number>> | null => {
		if (!selectedTile.value) return null;
		if (tilerepoNonSellableFrameNames.has(selectedTile.value.frameName))
			return null;
		if (tilerepoSalvageableFrameNames.has(selectedTile.value.frameName))
			return null;
		if (!selectedTile.value.upgrades.has(FrameName.NOT_SET)) return null;

		if (tilerepoFriendlyUnitsFrameNames.has(selectedTile.value.frameName)) {
			const { friendlyUnitDisbandReward } = selectedTile.value;
			if (!friendlyUnitDisbandReward) return null;
			return formatPrice(friendlyUnitDisbandReward);
		}

		const { reward } = selectedTile.value.upgrades.get(
			FrameName.NOT_SET,
		)! as UpgradeParams & { reward: Partial<Record<ResourceType, number>> }; // rewarda moze byt len number
		if (!reward) return null;

		const consumption = Array.from(selectedTile.value.consumption).map(
			([resourceType, value]: [ResourceType, number]): Partial<
				Record<ResourceType, number>
			> => formatPrice({ [resourceType]: value }),
		);

		const result = Object.assign({}, formatPrice(reward), ...consumption);

		[
			ResourceType.PRODUCTION_RESOURCE_UNIT,
			ResourceType.PRODUCTION_RESOURCE_MULTIPLIER,
			ResourceType.PRODUCTION_ONCLICK_RESOURCE_MULTIPLIER,
		].forEach((resourceType: ResourceType): void => {
			if (resourceType in result) delete result[resourceType];
		});

		return result;
	},
);

const tileEventType = (frameName: FrameName): EventCategory | undefined => {
	if (tilerepoEasterEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.EASTER;
	}
	if (tilerepoChristmasEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.CHRISTMAS;
	}
	if (tilerepoFishingEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.FISHING;
	}
	if (tilerepoFirefestEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.FIREFEST;
	}
	if (tilerepoSolarFairEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.SOLAR;
	}
	if (tilerepoHuntingEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.HUNTING;
	}
	if (tilerepoHalloweenEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.HALLOWEEN;
	}
	if (tilerepoPiratesEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.PIRATES;
	}
	return undefined;
};

const fewShipyards = (frameName: FrameName): boolean => {
	if (!selectedTile.value) return false;

	if (
		!seaFrames.has(selectedTile?.value.frameName) ||
		(!tilerepoFishingBoatsFrameNames.has(frameName) &&
			!tilerepoBigFishingBoatsFrameNames.has(frameName))
	)
		return false;

	const shipyard = tilerepo.getAShipyardEntity()!;
	const availableFishingBoats = shipyard?.availableFishingBoats ?? 0;

	return availableFishingBoats <= 0;
};

const hasEnoughtSettlementLevelForPremiumRoads = (
	frameName: FrameName,
): boolean => {
	if (!selectedTile.value) return false;

	const isGravelType =
		tilerepoGravelRoadsFrameNames.has(frameName) ||
		tilerepoWoodenBridgeRoadsFrameNames.has(frameName);

	const isCobbledType =
		tilerepoCobbledRoadsFrameNames.has(frameName) ||
		tilerepoTunnelRoadsFrameNames.has(frameName) ||
		tilerepoStoneBridgeRoadsFrameNames.has(frameName);

	if (!isGravelType && !isCobbledType) return false;

	const result = tilerepo.getCurrentNumberOfPremiumRoads();
	const caps = settlementsStore.getTotalAvailableRoadCaps;

	if (isGravelType) {
		return caps[FrameName.GRAVEL_ROAD] - result[FrameName.GRAVEL_ROAD] <= 0;
	}

	if (isCobbledType) {
		return (
			caps[FrameName.COBBLED_ROAD] - result[FrameName.COBBLED_ROAD] <= 0
		);
	}

	return false;
};

const computedEntities = computed((): Array<TileEntity> => {
	if (!selectedTile.value || isSettlement.value) return [];

	const order = [
		FrameName.NOT_SET,
		...Array.from(tilerepoEventBuildingsFrameNames),
		...Array.from(tilerepoRoadsFrameNames),
		...Array.from(tilerepoForestFrameNames),
		...Array.from(selectedTile.value.canUpgradeTo),
	].filter(
		(
			frameName: FrameName,
			index: number,
			self: Array<FrameName>,
		): boolean => self.indexOf(frameName) === index,
	);

	// ak nema ziadne odomknute upgrady
	if (selectedTile.value.canUpgradeTo.size === 0) {
		//  vytiahne a prvy upgrade zo vsetkych kvoli zamknutemu stavu
		const firstUpgradeEntry = selectedTile.value.upgrades
			.entries()
			.next().value;
		if (firstUpgradeEntry) {
			const entity = tilerepo.createEntityByFrameName(
				firstUpgradeEntry[0],
				{
					x: selectedTile.value!.x,
					y: selectedTile.value!.y,
				},
			);

			const requiredLevel = entity?.requirements.has(ResourceType.LEVEL)
				? (entity?.requirements.get(ResourceType.LEVEL) ?? 0)
				: 0;

			// zobrazit chceme iba ak user nema pozadovany level
			return entity &&
				requiredLevel > mainLevel.value &&
				![
					OFFER_BUILDING_UNLOCK_LEVEL,
					EVENT_BUILDING_UNLOCK_LEVEL,
				].includes(requiredLevel)
				? [entity]
				: [];
		}
	}

	return (
		Array.from(selectedTile.value.canUpgradeTo)
			.map((frameName: FrameName): TileEntity | null => {
				const entity = tilerepo.createEntityByFrameName(frameName, {
					x: selectedTile.value!.x,
					y: selectedTile.value!.y,
				});
				if (!entity) return null;

				entity.showProductionInfo = true;
				entity.showConsumptionInfo = true;
				return entity;
			})
			.filter(
				(entity: TileEntity | null): entity is TileEntity =>
					entity !== null && entity.frameName !== FrameName.GRASSLAND,
			)
			.sort(
				(
					{ frameName: aFrameName }: TileEntity,
					{ frameName: bFrameName }: TileEntity,
				): number =>
					order.indexOf(aFrameName) - order.indexOf(bFrameName),
			)
			// Cez tutorial, ak tam mame moznost stavat z cesty budovy, tak sa tutorial zrube, lebo sa computedEntities zmenia a znova sa triggerne tutorial focus layer cez isTutorialGlow().
			.filter((entity: TileEntity | null): entity is TileEntity => {
				if (questStore.tutorialActive)
					return entity!.frameName === FrameName.DIRT_ROAD; // Toto ma byt po spravnosti FrameName.DIRT_ROAD_8, ale z nejakeho dovodu tu musi byt tato abstraktna verzia.
				return true;
			})
	);
});
const limitReached = (entity: TileEntity): boolean => {
	return (
		!!entity.buildingLimit &&
		entity.sameBuildingCount >= entity.buildingLimit
	);
};
const handleVisitShop = (): void => {
	if (!selectedTile.value) return;
	// NOTE: deselect tile visually when redirecting
	emitter.emit(EmitterEvents.PhaserTileDeselect);
	router.push({
		name: RouteNames.SHOP,
		params: { scrollTo: missingResource.value },
	});
};

const isAvailableSettlementUpgrade = ref<boolean>(false);
const handleSettlementAvailableUpgrade = (isAvailable: boolean): void => {
	isAvailableSettlementUpgrade.value = isAvailable;
};

const switchParagraphFormat = (text: string): string => {
	let paragraphTranslated = text;
	paragraphTranslated = paragraphTranslated.replace("{b}", "");
	paragraphTranslated = paragraphTranslated.replace("{/b}", "");
	return paragraphTranslated;
};

const lowGems = (frameName: FrameName, isSkip?: boolean): boolean => {
	if (tilerepoPremiumBuildings.has(frameName)) return false;

	const gemsPrice =
		selectedTile.value?.resolveDynamicResources({
			[ResourceType.GEMS]: isSkip
				? (selectedTile.value?.skipDefeatGemsPrice ?? 0)
				: (selectedTile.value?.upgrades?.get(frameName)?.price?.[
						ResourceType.GEMS
					] ?? 0),
		}) || {};
	if (gemsPrice[ResourceType.GEMS])
		return gemsPrice[ResourceType.GEMS] > gems;

	return false;
};
const showPutInInventoryIcon = computed((): boolean => {
	if (!selectedTile.value) return false;
	if (
		tilerepoRemovableEventBuildingsFrameNames.has(
			selectedTile.value.frameName,
		) ||
		tilerepoNotStorableFrameNames.has(selectedTile.value.frameName)
	)
		return false;

	return selectedTile.value.isStorable && !showPutInInInventoryButton.value;
});

const showPutInInInventoryButton = computed((): boolean => {
	if (!selectedTile.value) return false;
	if (
		tilerepoRemovableEventBuildingsFrameNames.has(
			selectedTile.value.frameName,
		) ||
		tilerepoNotStorableFrameNames.has(selectedTile.value.frameName)
	)
		return false;

	return (
		selectedTile.value.isStorable &&
		!tilerepoPremiumBuildings.has(selectedTile.value.frameName) &&
		!tilerepoNonSellableFrameNames.has(selectedTile.value.frameName)
	);
});

const handlePutInInventory = (): void => {
	if (!selectedTile.value) return;
	handleUpgrade(
		selectedTile.value.isCuttable || selectedTile.value.isRemovable
			? FrameName.GRASSLAND
			: FrameName.NOT_SET,
		false,
		"",
		undefined,
		true,
	);
};

const move = (): void => {
	if (!selectedTile.value) return;
	sss.setMovingUnitsIndex(selectedTile.value?.index);

	setBuilding(selectedTile.value.frameName);
	setAction(QuickToolActions.BUILD);
	setActive(true);
	setShow(true);
	setError(false);
	router.push({
		name: RouteNames.LAYOUT_VIEW,
	});
};

const handleUpgrade = (
	frameName: FrameName,
	isUpgradeBox: boolean = false,
	entityName: string = "",
	isSkip?: boolean,
	storeToInventory: boolean = false,
	limitReached: boolean = false,
): void => {
	if (!selectedTile.value) return;
	if (limitReached) {
		return;
	}
	if (
		isUpgradeBox &&
		(!selectedTile.value.upgrades.has(frameName) ||
			fewShipyards(frameName) ||
			(selectedTile.value.isUpgradeToDisabled(frameName) &&
				!lowGems(frameName)) ||
			hasEnoughtSettlementLevelForPremiumRoads(frameName))
	)
		return;
	if (lowGems(frameName)) {
		router.push({ name: RouteNames.SHOP });
		return;
	}

	const gemsPrice =
		selectedTile.value?.resolveDynamicResources({
			[ResourceType.GEMS]: isSkip
				? (selectedTile.value?.skipDefeatGemsPrice ?? 0)
				: (selectedTile.value?.upgrades?.get(frameName)?.price?.[
						ResourceType.GEMS
					] ?? 0),
		}) || {};

	const isPremiumBuilding =
		tilerepoPremiumBuildings.has(frameName) &&
		!!gemsPrice[ResourceType.GEMS];

	if (
		isPremiumBuilding &&
		showConfirmGemsMessages.value &&
		buildingsStorageStore?.[frameName] === 0
	) {
		popupStore.showPopup({
			id: PopupDictionary.PREMIUM_BUILDING,
			type: PopupType.INFO,
			heading: translate("ui.premium_building"),
			body: {
				ctaText: translate("ui.building_confirm_message"),
				ctaText2: entityName,
				price: gemsPrice,
			},
			skin: SkinTypes.PREMIUM,
			showSkipMessageNotice: true,
			actions: [
				{
					label: translate("button.buy"),
					buttonType: StatusTypes.PRIMARY,
					callback: async (): Promise<void> => {
						if (!selectedTile.value) return;

						const prevFrameName = selectedTile.value.frameName;
						const nextFrameName = frameName;

						turnOnQuickTool(
							prevFrameName,
							nextFrameName,
							selectedTile.value as TileEntity,
							isSkip,
							storeToInventory,
						);

						selectedTile.value = tilerepo.handleUpgrade(
							prevFrameName,
							nextFrameName,
							selectedTile.value as TileEntity,
							undefined,
							storeToInventory,
						);

						router.push({
							name: RouteNames.LAYOUT_VIEW,
						});
						popupStore.hidePopup();
					},
				},
				{
					label: translate("button.cancel"),
					buttonType: StatusTypes.DEFAULT,
					callback: async (): Promise<void> => {
						popupStore.hidePopup();
					},
				},
			],

			maxHeight: 20,
			isPaddingless: true,
		});
		return;
	}
	const prevFrameName = selectedTile.value.frameName;
	const nextFrameName = frameName;

	// ak sa upgrade podaril, zapneme QT
	if (selectedTile.value) {
		turnOnQuickTool(
			prevFrameName,
			nextFrameName,
			selectedTile.value as TileEntity,
			isSkip,
			storeToInventory,
		);
	}
	switch (selectedTile.value.action) {
		case TileAction.SAVE_MATERIAL:
			soundService.playSaveMaterial();
			break;
		case TileAction.DEMOLISH:
			soundService.playSoundDemolition();
			break;
		default:
			break;
	}
	// During tutorial, disable overlay after clicking on upgrade
	if (isTutorialHighlightingUI.value) setIsTutorialHighlightingUI(false);

	selectedTile.value = tilerepo.handleUpgrade(
		prevFrameName,
		nextFrameName,
		selectedTile.value as TileEntity,
		isSkip,
		storeToInventory,
	);

	router.push({
		name: RouteNames.LAYOUT_VIEW,
	});
};

const setCurrentTile = (x: number, y: number): void => {
	const tempSelectedTile = selectedTile.value;
	if (!isPhaserReady.value) return;

	selectedTile.value = tilerepo.getTileEntityByTileCoordinates({
		x,
		y,
	});

	if (!tempSelectedTile && selectedTile.value) {
		emitter.emit(EmitterEvents.PhaserTileSelect, {
			x: selectedTile.value!.x,
			y: selectedTile.value!.y,
		});
	}
};

const onTileRepositoryUpgrade = ({
	frameName,
}: TileRepositoryUpgradePayload): void => {
	const x = +route.params.x;
	const y = +route.params.y;
	if (x === selectedTile.value?.x && y === selectedTile.value?.y) return;

	// eslint-disable-next-line no-console
	console.log("[edit]", frameName);
	// NOTE: required when staying on route but params are changing (deforestation, wheat growth)
	setCurrentTile(x, y);
};

const onGameTick = (): void => {
	if (!selectedTile.value) return;

	// refresh entity on game tick
	selectedTile.value = tilerepo.getTileEntityByTileCoordinates({
		x: selectedTile.value.x,
		y: selectedTile.value.y,
	});
};

const useBribe = (): void => {
	if (!selectedTile.value) return;
	handleUpgrade(FrameName.NOT_SET, false, selectedTile.value.name, true);
};

const openBribePopup = (): void => {
	if (!selectedTile.value?.skipDefeatGemsPrice) return;

	popupStore.showPopup({
		id: PopupDictionary.BRIBE,
		type: PopupType.INFO,
		heading: translate("ui.bribe_title"),
		height: 12,
		isCentered: true,
		component: markRaw(BribePopup),
		componentProps: {
			gemsPrice: selectedTile.value.skipDefeatGemsPrice as number,
			isDisabled: lowGems(FrameName.NOT_SET, true),
			onBribe: useBribe,
		},
	});
};

onMounted((): void => {
	if (!route.params.x || !route.params.y) return;
	setCurrentTile(+route.params.x, +route.params.y);

	emitter.on(EmitterEvents.TileRepositoryUpgrade, onTileRepositoryUpgrade);
	emitter.on(EmitterEvents.GameTick, onGameTick);
});

onBeforeUnmount((): void => {
	emitter.off(EmitterEvents.TileRepositoryUpgrade, onTileRepositoryUpgrade);
	emitter.off(EmitterEvents.GameTick, onGameTick);
});

const isTutorialGlow = (): boolean => {
	const result = tutorialStep.value >= 5 && tutorialStep.value < 8;
	setIsTutorialHighlightingUI(result);
	return result;
};

const isStorable = (frameName: FrameName): boolean => {
	if (tilerepoRemovableEventBuildingsFrameNames.has(frameName)) {
		return isTemporaryEventBuildingActive(frameName);
	}
	return (
		tilerepoPremiumBuildings.has(frameName) ||
		tilerepoNonSellableFrameNames.has(frameName)
	);
};

const priceValue = (
	entity: TileEntity,
): Partial<Record<ResourceType, number>> =>
	selectedTile.value?.resolveDynamicResources(
		selectedTile.value?.upgrades?.get(entity.frameName)?.price ?? {},
	) ?? {};

watch(
	(): boolean => isSettlement.value,
	(newVal: boolean): void => {
		if (newVal) {
			route.params.isSettlement = isSettlement.value.toString();
		}
	},
);
</script>

<style lang="scss" scoped>
.tile-detail-view {
	overflow-x: hidden;

	.settlement-detail {
		flex: 1 1 0;
		flex-direction: column;

		:deep(.tabs) {
			display: flex;
			flex: 1 1 0;
			flex-direction: column;
		}

		:deep(.tabs__content) {
			flex: 1 1 auto;
		}

		&--stats {
			:deep(.tabs__content) {
				padding: 0;
			}
		}
	}

	&__body {
		flex: 1 1 auto;
		width: 100%;

		.badge {
			border: 0.125rem solid #dfdfdf;
			border-radius: 1rem;
			padding: 0.125rem 0.5rem;
		}
	}

	&__section {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		padding-top: 0.125rem; // for building TOP overlap
		padding-bottom: 0.5rem;
		width: 100%;

		&-box {
			@include flex-row-start;
			align-items: flex-start;
			gap: 0.5rem;
			padding-left: 0.25rem;
			width: 100%;

			&--fight-system {
				padding-left: 0;
			}
		}

		&-content {
			@include flex-col-start;
			flex: 1 1 auto;
			align-items: flex-start;
			gap: 0.375rem;
			padding-top: 0.25rem;

			&.section-content {
				&--slim {
					@include flex-row-start;
					gap: 0.25rem;

					.tile-detail-view__section-description {
						display: flex;
						font-size: 0.75rem;
					}

					.tile-detail-view__section-description {
						color: #fff3dd;
					}
				}
			}
		}

		&-image {
			flex-shrink: 0;
			margin-top: -0.5rem;

			&--event {
				margin-top: 0;
			}
		}

		&-label {
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			line-height: 1rem;
			text-shadow: 0.07rem 0.07rem 0.1rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&-description {
			color: #fff3dd;
			font-size: 0.875rem;
			line-height: 1rem;
			text-align: left;
			text-shadow: 0.07rem 0.07rem 0.1rem rgba(0, 0, 0, 0.6);
		}
		&-limit {
			font-weight: bold;
			@include flex-row-start;
			gap: 0.25rem;
			.full-limit {
				color: #ff3d3d;
			}
		}
	}

	.option-collection {
		@include flex-col-center;
		gap: 0.5rem;
		padding: 0 0.25rem;

		&__items {
			&--grid {
				@include flex-row-start;
				flex-wrap: wrap;
				gap: 0.5rem;
				width: 100%;

				> * {
					flex: 0 0 calc(25% - 0.5rem);
				}
			}

			&--list {
				@include flex-col-center;
				gap: 0.5rem;
				width: 100%;
			}
		}
	}

	&__top-bar {
		@include flex-row-center;
		position: relative;
		gap: 0.3rem;
		width: 100%;
		min-width: 9rem;
		height: 100%;

		:deep(.text-button) {
			padding: 0.4375rem 0.5rem 0.3125rem;
			width: auto;
			height: 2rem;
		}
	}
	.remove-section {
		@include flex-row-start;
		gap: 0.5rem;
		width: 100%;
	}

	.event-building-label {
		@include flex-row-center;
		position: relative;
		align-self: stretch;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		margin: 0.25rem 0;
		width: 100%;
		height: 1.875rem;
		span {
			position: relative;
			z-index: 1;
			padding-bottom: 0.25rem;
			color: #f4d278;
			font-weight: 800;
			font-size: 0.906rem;
			line-height: 0.906rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
		:deep(.app-button-info) {
			transform: scale(0.9);
			z-index: 1;
			margin-left: 0.25rem;
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 0;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			mix-blend-mode: hard-light;
			width: 100%;
			height: 100%;
			content: "";
		}
		&[data-event-type="christmas"] {
			&::after {
				background: hsl(136, 79%, 15%);
			}
		}
		&[data-event-type="easter"] {
			span {
				color: #fff3dd;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(215, 154, 36, 0.05) 0%,
					rgba(215, 154, 36, 0.8) 15%,
					rgba(215, 154, 36, 1) 30%,
					rgba(215, 154, 36, 1) 70%,
					rgba(215, 154, 36, 0.8) 85%,
					rgba(215, 154, 36, 0.05) 100%
				);
			}
		}
		&[data-event-type="fishing"] {
			span {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(91, 115, 9, 0.05) 0%,
					rgba(91, 115, 9, 0.8) 15%,
					rgba(91, 115, 9, 1) 30%,
					rgba(91, 115, 9, 1) 70%,
					rgba(91, 115, 9, 0.8) 85%,
					rgba(91, 115, 9, 0.05) 100%
				);
			}
		}
		&[data-event-type="firefest"] {
			span {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(122, 74, 148, 0) 0%,
					rgba(122, 74, 148, 0.8) 15%,
					rgba(122, 74, 148, 1) 30%,
					rgba(122, 74, 148, 1) 60%,
					rgba(122, 74, 148, 0.8) 85%,
					rgba(122, 74, 148, 0) 100%
				);
			}
		}
		&[data-event-type="solarfair"] {
			span {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(122, 74, 148, 0) 0%,
					rgba(122, 74, 148, 0.8) 15%,
					rgba(122, 74, 148, 1) 30%,
					rgba(122, 74, 148, 1) 60%,
					rgba(122, 74, 148, 0.8) 85%,
					rgba(122, 74, 148, 0) 100%
				);
			}
		}
		&[data-event-type="hunting"] {
			span {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(122, 74, 148, 0) 0%,
					rgba(122, 74, 148, 0.8) 15%,
					rgba(122, 74, 148, 1) 30%,
					rgba(122, 74, 148, 1) 60%,
					rgba(122, 74, 148, 0.8) 85%,
					rgba(122, 74, 148, 0) 100%
				);
			}
		}
		&[data-event-type="halloween"] {
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(91, 115, 9, 0.05) 0%,
					rgba(91, 115, 9, 0.8) 15%,
					rgba(91, 115, 9, 1) 30%,
					rgba(91, 115, 9, 1) 70%,
					rgba(91, 115, 9, 0.8) 85%,
					rgba(91, 115, 9, 0.05) 100%
				);
			}
		}
		&[data-event-type="pirates"] {
			span {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(91, 115, 9, 0.05) 0%,
					rgba(91, 115, 9, 0.8) 15%,
					rgba(91, 115, 9, 1) 30%,
					rgba(91, 115, 9, 1) 70%,
					rgba(91, 115, 9, 0.8) 85%,
					rgba(91, 115, 9, 0.05) 100%
				);
			}
		}
	}

	.demolish-section {
		flex-grow: 1;
		cursor: pointer;
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #181a1d, #303239);
		height: 3.75rem;
		overflow: hidden;

		&__wrapper {
			@include flex-row-start;
			gap: 0.75rem;
			padding: 0.5625rem 0.5625rem 0.5625rem 0.6875rem;
			height: 3.5rem;
		}
		&--disabled {
			opacity: 0.5;
			cursor: not-allowed;
			pointer-events: none;
		}

		&__icon {
			flex-shrink: 0;
			margin-top: -0.125rem;
			width: 1.875rem;
			height: 1.875rem;
		}

		&__content {
			@include flex-col-center;
			align-items: flex-start;
			gap: 0.375rem;
			margin-top: -0.25rem;

			.demolish-section {
				&__label {
					color: #fff3dd;
					font-weight: 800;
					font-size: 1rem;
					line-height: 1rem;
					text-align: center;
					text-shadow: 0.044rem 0.044rem 0rem rgba(0, 0, 0, 0.6);
				}

				&__price {
					height: 0.875rem;
					color: #fff3dd;
					font-size: 0.875rem;
					text-align: center;
					text-shadow: 0.044rem 0.044rem 0.0625rem rgba(0, 0, 0, 0.6);
					@include flex-row-center;

					& > * {
						line-height: 0.875rem;
					}

					.price-label {
						margin-right: 0.25rem;
					}

					.price:not(:last-child) {
						margin-right: 0.5rem;
					}
				}
			}
		}
	}
	.put-in-inventory {
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #181a1d, #303239);
		width: 10rem;
		height: 3.75rem;
	}
	.move-button {
		width: 3.75rem;
		height: 3.75rem;
		@include flex-row-center;
		flex-shrink: 0;
		cursor: pointer;
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(
			to top,
			rgb(189, 144, 50),
			rgb(240, 211, 134)
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to top,
			rgb(29, 82, 97),
			rgb(43, 145, 173)
		);
	}
	.out-of-order-section {
		@include flex-col-center;
		position: relative;
		box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border: solid 0.063rem #cf0032;
		background-color: rgba($color: #550014, $alpha: 0.5);
		padding: 0.5rem 1rem;
		width: 100%;
		min-height: 4rem;
		text-align: center;
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1 !important;
			// NOTE: Multiply blending is better for light colors
			mix-blend-mode: multiply !important;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat left;
			background-size: 80%;
			width: 100%;
			height: 100%;
			content: "";
		}

		& > span {
			color: #cf0032;
			font-size: 0.906rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;

			&.no-uppercase {
				text-transform: none;
			}
		}
	}

	:deep(.modal-window-wrapper) {
		z-index: 2;
	}

	:deep(.upgrade-box__heading) {
		align-items: initial;

		& > span {
			top: 0.2rem;
		}
	}

	:deep(.upgrade-box__content) {
		position: relative;
		bottom: 0.25rem;
		padding-right: 0;
	}

	&__upgrade {
		@include flex-col-center;
		gap: 0.25rem;
		padding: 0 0.25rem;
		padding-top: 0;
		width: 100%;

		&-heading {
			color: #f2dcb5;
			font-weight: bold;
			font-size: 1rem;
		}
	}

	&__blocked-by-enemy {
		width: 100%;
		@include flex-row-center;
		opacity: 0.76;
		box-shadow: inset 0 0 1.875rem 0 rgba(29, 30, 33, 0.8);
		border-width: 0.125rem;
		border-style: solid;
		border-image-source: linear-gradient(
			to top,
			rgb(209, 8, 15),
			rgb(209, 8, 15)
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to top,
			rgb(145, 9, 42),
			rgb(145, 9, 42)
		);
		height: 4rem;
		color: #ff3d3d;
	}

	// IMPORTANT: ensuring that only the tab content is scrollable, not the wrapper above it
	:deep(.modal-window__content) {
		.scroll-basic-container {
			display: flex;
			flex-direction: column;
			min-height: 0;

			.settlement-detail {
				flex: 1 1 auto;
				min-height: 0;

				.tabs {
					flex: 1 1 auto;
					min-height: 0;

					.scroll-fade-container {
						flex: 1 1 auto;
						min-height: 0;
					}
				}
			}
		}
	}
}
</style>
